import { ProgramInitiationAccountMappingDto } from '../../api/generated/api.generated'

export interface AccountMappingData {
  sfAccountId: string
  sfAccountName: string
  carrierId: number
  carrierName?: string
}

/**
 * The SF account first mapped to a carrier in carrier_sfaccount_bridge. Mappings with no
 * audit_insert_dt predate auditing, so they count as earliest; ties break on account name.
 */
export function earliestMappedAccount(
  mappings: ProgramInitiationAccountMappingDto[],
  carrierId: number,
): ProgramInitiationAccountMappingDto | undefined {
  return mappings
    .filter((m) => m.carrierId === carrierId)
    .sort(
      (a, b) =>
        (a.mappedDate ?? '').localeCompare(b.mappedDate ?? '') ||
        a.sfAccountName.localeCompare(b.sfAccountName),
    )[0]
}

export type AccountOpportunitySortColumn =
  | 'accountName'
  | 'opportunityName'
  | 'opportunityStage'
  | 'opportunityBU'
  | 'opportunityClass'
  | 'opportunitySubClass'
  | 'opportunityRiskType'
  | 'opportunityInceptionDate'
  | 'initiatedDate'

export type SortDirection = 'asc' | 'desc'

/** Server-side filters for api/program-initiation/account-opportunities. Unset = no filter. */
export interface AccountOpportunityFilters {
  accountName: string
  opportunityName: string
  opportunityStage: string
  opportunityBU: string
  opportunityClass: string
  opportunitySubClass: string
  opportunityRiskType: string
  initiated: boolean | null
}

export interface AccountOpportunityQuery extends AccountOpportunityFilters {
  sortBy: AccountOpportunitySortColumn
  sortDirection: SortDirection
  /** 1-based */
  page: number
  pageSize: number
}

export const EMPTY_ACCOUNT_OPPORTUNITY_FILTERS: AccountOpportunityFilters = {
  accountName: '',
  opportunityName: '',
  opportunityStage: '',
  opportunityBU: '',
  opportunityClass: '',
  opportunitySubClass: '',
  opportunityRiskType: '',
  initiated: null,
}

export const DEFAULT_ACCOUNT_OPPORTUNITY_PAGE_SIZE = 50

/** Text filters and account-name suggestions only apply once this many characters are typed */
export const MIN_SEARCH_LENGTH = 3

/** The list only loads once at least one filter is set (or the user explicitly chooses Show All) */
export function hasActiveFilters(filters: AccountOpportunityFilters): boolean {
  return (
    filters.initiated !== null ||
    !!filters.accountName ||
    !!filters.opportunityName ||
    !!filters.opportunityStage ||
    !!filters.opportunityBU ||
    !!filters.opportunityClass ||
    !!filters.opportunitySubClass ||
    !!filters.opportunityRiskType
  )
}
